import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { del, get, post, put } from '../../controllers/apiController';
import { Select2WColor } from '../../components/Select2';
import { useAuth } from '../../context/AuthContext';
import { formatPhoneTR } from '../../utils/phone';
import { parseCommaList, sanitizeList } from '../../utils/list';
import MultiTextInput from '../../components/MultiTextInput';

import Swal from 'sweetalert2';
import { getProject, getProjectLabel, withProject } from '../../utils/project';

const AcenteDetay = () => {
    const { user } = useAuth();
    const isAdmin = user.Kullanici_Yetki == 0;

    const [searchParams] = useSearchParams();
    const [acenteId] = useState(searchParams.get("id"));

    const [acente, setAcente] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [etiketlerRaw, setEtiketlerRaw] = useState([]);
    const [etiketOptions, setEtiketOptions] = useState([]);
    const [temsilciler, setTemsilciler] = useState([]);

    const [notes, setNotesData] = useState([]);
    const [selectedNote, setSelectedNote] = useState(null);
    const [noteTitle, setNoteTitle] = useState('');
    const [noteDesc, setNoteDesc] = useState('');

    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState(null);

    const [yeniAciklama, setYeniAciklama] = useState('');
    const [reminderDate, setReminderDate] = useState('');
    const [newEtiket, setNewEtiket] = useState(null);

    const editAciklamaRef = useRef(null);
    const editReminderRef = useRef(null);
    const editTemsilciRef = useRef(null);
    const addEtiketRef = useRef(null);
    const addNoteRef = useRef(null);
    const viewNoteRef = useRef(null);
    const temsilciSelectRef = useRef(null);

    const formatToDateTimeLocal = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const formatToDateInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateTimeDisplay = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '');
    };

    const parseEtiketIds = (etiketlerStr) => {
        if (!etiketlerStr) return [];
        return etiketlerStr.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
    };

    useEffect(() => {
        const fetchAcente = async () => {
            try {
                const res = await get(`acenteler/${acenteId}`, { project: getProject() });

                if (res && !res.message) {
                    setAcente(res);
                    setNotesData(res.Notlar || []);
                    setYeniAciklama(res.Aciklama || '');
                    setReminderDate(formatToDateTimeLocal(res.Hatirlatma_Tarihi));

                    document.title = `${res.Acente_Adi} | LEVELUP CRM`;
                } else {
                    document.title = "Bulunamadı | LEVELUP CRM";
                }
            } catch (err) {
                toast.error("Acente bilgileri yüklenirken bir hata oluştu!");
            }
        }

        const fetchEtiketler = async () => {
            try {
                const res = await get("labels/");

                setEtiketlerRaw(res);
                setEtiketOptions(res.map(etiket => ({
                    value: etiket.Etiket_ID,
                    label: etiket.Etiket_Adi,
                    color: "#" + etiket.Etiket_Renk
                })));
            } catch (err) {
                toast.error("Etiket verileri alınırken bir hata oluştu!");
            }
        }

        const fetchTemsilciler = async () => {
            try {
                const res = await get("users/company-users");

                setTemsilciler(res.map(temsilci => ({
                    value: temsilci.Kullanici_ID,
                    label: `${temsilci.Kullanici_Adi} ${temsilci.Kullanici_Soyadi}`
                })));
            } catch (err) {
                toast.error("Temsilci verileri alınırken bir hata oluştu!");
            }
        }

        const fetchAll = async () => {
            await Promise.all([fetchAcente(), fetchEtiketler(), fetchTemsilciler()]);
            setIsLoading(false);
        }

        if (acenteId) {
            fetchAll();
        }
    }, [acenteId]);

    useEffect(() => {
        // Modal açıldığında body'deki modal-open class'ını kaldır
        const removeModalOpenClass = () => {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'auto';
            document.body.style.paddingRight = '0';
        };

        const editReminderModal = document.getElementById('editReminder');
        const editAciklamaModal = document.getElementById('editAciklama');
        const editTemsilciModal = document.getElementById('editTemsilci');
        const addEtiketModal = document.getElementById('addEtiket');
        const addNoteModal = document.getElementById('addNote');
        const viewNoteModal = document.getElementById('viewNote');

        editReminderModal?.addEventListener('shown.bs.modal', removeModalOpenClass);
        editAciklamaModal?.addEventListener('shown.bs.modal', removeModalOpenClass);
        editTemsilciModal?.addEventListener('shown.bs.modal', removeModalOpenClass);
        addEtiketModal?.addEventListener('shown.bs.modal', removeModalOpenClass);
        addNoteModal?.addEventListener('shown.bs.modal', removeModalOpenClass);
        viewNoteModal?.addEventListener('shown.bs.modal', removeModalOpenClass);

        return () => {
            editReminderModal?.removeEventListener('shown.bs.modal', removeModalOpenClass);
            editAciklamaModal?.removeEventListener('shown.bs.modal', removeModalOpenClass);
            editTemsilciModal?.removeEventListener('shown.bs.modal', removeModalOpenClass);
            addEtiketModal?.removeEventListener('shown.bs.modal', removeModalOpenClass);
            addNoteModal?.removeEventListener('shown.bs.modal', removeModalOpenClass);
            viewNoteModal?.removeEventListener('shown.bs.modal', removeModalOpenClass);
        };
    }, []);

    useEffect(() => {
        if (selectedNote != null) {
            const viewNoteDesc = document.getElementById("viewNoteDesc");

            viewNoteDesc.style.height = 'auto';
            viewNoteDesc.style.height = viewNoteDesc.scrollHeight + 'px';
        }
    }, [selectedNote]);

    const openEditModal = () => {
        const parsedTemsilciAdi = parseCommaList(acente.Temsilci_Adi);

        setEditForm({
            agencyName: acente.Acente_Adi || '',
            representativeNames: parsedTemsilciAdi.length > 0 ? parsedTemsilciAdi : [''],
            nationality: acente.Uyruk || '',
            phone: acente.Telefon || '',
            email: acente.Email || '',
            date: formatToDateInput(acente.Son_Arama_Tarihi)
        });
        setShowEditModal(true);
    }

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (editForm.agencyName === "") {
            toast.error('Acente adı zorunludur');
            return;
        }

        try {
            const representativeNames = sanitizeList(editForm.representativeNames);

            await put('acenteler', {
                project: getProject(),
                agencyId: acente.Acente_ID,
                agencyName: editForm.agencyName,
                representativeNames,
                nationality: editForm.nationality,
                phone: editForm.phone,
                email: editForm.email,
                date: editForm.date || null
            });

            setAcente(prev => ({
                ...prev,
                Acente_Adi: editForm.agencyName,
                Temsilci_Adi: representativeNames,
                Uyruk: editForm.nationality,
                Telefon: editForm.phone,
                Email: editForm.email,
                Son_Arama_Tarihi: editForm.date || null
            }));

            setShowEditModal(false);
            toast.success('Acente başarıyla güncellendi');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Acente güncellenirken bir hata oluştu');
        }
    }

    const handleAciklamaUpdate = async () => {
        try {
            await put('acenteler/change-description', {
                agencyId: acente.Acente_ID,
                description: yeniAciklama.trim()
            });

            setAcente(prev => ({ ...prev, Aciklama: yeniAciklama.trim() }));
            toast.success("Açıklama başarıyla güncellendi");

            editAciklamaRef.current.querySelector('[data-bs-dismiss="modal"]').click();
        } catch (err) {
            toast.error("Açıklama güncellenirken hata oluştu");
        }
    };

    const handleReminderUpdate = async () => {
        try {
            await put('acenteler/change-reminder', {
                agencyId: acente.Acente_ID,
                reminderDate: reminderDate
            });

            setAcente(prev => ({ ...prev, Hatirlatma_Tarihi: reminderDate }));
            toast.success("Hatırlatma tarihi başarıyla güncellendi");

            editReminderRef.current.querySelector('[data-bs-dismiss="modal"]').click();
        } catch (err) {
            toast.error("Hatırlatma tarihi güncellenirken hata oluştu");
        }
    };

    const deleteReminder = async () => {
        try {
            await put('acenteler/change-reminder', {
                agencyId: acente.Acente_ID
            });

            setAcente(prev => ({ ...prev, Hatirlatma_Tarihi: null }));
            toast.success("Hatırlatma tarihi başarıyla kaldırıldı");
        } catch (err) {
            toast.error("Hatırlatma tarihi kaldırılırken hata oluştu");
        }
    }

    const changeTemsilci = async () => {
        try {
            const userID = temsilciSelectRef.current.value || null;

            await put('acenteler/change-user', {
                agencyId: acente.Acente_ID,
                userID: userID
            });

            const userData = userID ? await get(`users/company-users/${userID}`) : null;

            setAcente(prev => ({
                ...prev,
                Temsilci_ID: userData ? userData.Kullanici_ID : null
            }));

            toast.success("Temsilci başarıyla değiştirildi.");

            editTemsilciRef.current.querySelector('[data-bs-dismiss="modal"]').click();
        } catch (err) {
            toast.error(err.response?.data?.message || "Temsilci değiştirilirken bir hata oluştu!");
        }
    }

    const deleteTemsilci = async () => {
        try {
            await put('acenteler/change-user', {
                agencyId: acente.Acente_ID
            });

            setAcente(prev => ({ ...prev, Temsilci_ID: null }));

            toast.success("Temsilci başarıyla kaldırıldı.");
        } catch (err) {
            toast.error(err.response?.data?.message || "Temsilci kaldırılırken bir hata oluştu!");
        }
    }

    const addEtiket = async () => {
        if (!newEtiket) return;

        try {
            const currentIds = parseEtiketIds(acente.Etiketler);

            if (currentIds.length === 0) {
                await put('acenteler/change-labels', {
                    project: getProject(),
                    agencyId: acente.Acente_ID,
                    labelIds: [newEtiket.value]
                });
            } else {
                await put('acenteler/add-label', {
                    project: getProject(),
                    agencyId: acente.Acente_ID,
                    labelID: newEtiket.value
                });
            }

            setAcente(prev => ({
                ...prev,
                Etiketler: [...currentIds, newEtiket.value].join(',')
            }));

            setNewEtiket(null);
            toast.success("Etiket başarıyla eklendi.");
            addEtiketRef.current.querySelector('[data-bs-dismiss="modal"]').click();
        } catch (err) {
            toast.error(err.response?.data?.message || "Etiket eklenirken bir hata oluştu!");
        }
    }

    const deleteEtiket = async (labelId) => {
        Swal.fire({
            title: 'Etiketi silmek istediğinize emin misiniz?',
            icon: 'error',
            showCancelButton: true,
            confirmButtonText: 'Sil',
            cancelButtonText: 'İptal'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await put('acenteler/delete-label', {
                        project: getProject(),
                        agencyId: acente.Acente_ID,
                        labelID: labelId
                    });

                    setAcente(prev => ({
                        ...prev,
                        Etiketler: parseEtiketIds(prev.Etiketler).filter(id => id !== labelId).join(',')
                    }));

                    toast.success("Etiket başarıyla silindi.");
                } catch (err) {
                    toast.error(err.response?.data?.message || "Etiket silinirken bir hata oluştu!");
                }
            }
        });
    }

    const createNote = async () => {
        if (noteTitle === "" || noteDesc === "") {
            toast.error("Tüm alanlar doldurulmalıdır!");
            return;
        }

        try {
            const response = await post('acenteler/note', {
                project: getProject(),
                agencyId: acente.Acente_ID,
                title: noteTitle,
                desc: noteDesc
            });

            setNotesData([...notes, response]);

            setAcente(prev => ({
                ...prev,
                Arama_Sayisi: response.Arama_Sayisi,
                Son_Arama_Tarihi: response.Son_Arama_Tarihi
            }));

            addNoteRef.current.querySelector('[data-bs-dismiss="modal"]').click();

            setNoteTitle("");
            setNoteDesc("");

            toast.success("Not başarıyla oluşturuldu.");
        } catch (err) {
            toast.error(err.response?.data?.message || "Bilinmeyen bir hata oluştu!");
        }
    }

    const deleteNote = async (noteID) => {
        Swal.fire({
            title: 'Notu silmek istediğinize emin misiniz?',
            icon: 'error',
            showCancelButton: true,
            confirmButtonText: 'Sil',
            cancelButtonText: 'İptal'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await del(`acenteler/note/${noteID}`);

                    setNotesData(notes.filter((not) => not.Not_ID !== noteID));

                    setAcente(prev => ({
                        ...prev,
                        Arama_Sayisi: response.Arama_Sayisi,
                        Son_Arama_Tarihi: response.Son_Arama_Tarihi
                    }));

                    toast.success("Not başarıyla silindi.");
                } catch (err) {
                    toast.error(err.response?.data?.message || "Not silinirken bir hata oluştu!");
                }
            }
        });
    }

    const viewNote = (noteID) => {
        const noteData = notes.find((not) => not.Not_ID === noteID);

        setSelectedNote(noteData);
    }

    const modalBackdropStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1040
    };

    const currentEtiketIds = acente ? parseEtiketIds(acente.Etiketler) : [];
    const currentEtiketler = etiketlerRaw.filter(etiket => currentEtiketIds.includes(etiket.Etiket_ID));
    const temsilciLabel = acente && acente.Temsilci_ID
        ? (temsilciler.find(t => t.value === acente.Temsilci_ID)?.label || '')
        : '';

    return (
        <Layout>
            <main className="nxl-container">
                <div className="nxl-content">
                    <div className="page-header">
                        <div className="page-header-left d-flex align-items-center">
                            <div className="page-header-title">
                                <h5 className="m-b-10">LEVELUP CRM</h5>
                            </div>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item"><a href="./dashboard">Anasayfa</a></li>
                                <li className="breadcrumb-item"><a href={withProject("./agencies")}>Acente Yönetimi {getProjectLabel()}</a></li>
                                <li className="breadcrumb-item">Detay</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-white py-3 border-bottom rounded-0 p-md-0 mb-0">
                        <div className="d-md-none d-flex">
                            <a href="javascript:void(0)" className="page-content-left-open-toggle">
                                <i className="feather-align-left fs-20"></i>
                            </a>
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="nav-tabs-wrapper page-content-left-sidebar-wrapper">
                                <div className="d-flex d-md-none">
                                    <a href="javascript:void(0)" className="page-content-left-close-toggle">
                                        <i className="feather-arrow-left me-2"></i>
                                        <span>Geri</span>
                                    </a>
                                </div>
                                <ul className="nav nav-tabs nav-tabs-custom-style" id="myTab" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link active" data-bs-toggle="tab" data-bs-target="#infoTab">Bilgiler</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" data-bs-toggle="tab" data-bs-target="#notesTab">Görüşme Notları</button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="main-content">
                        <div className="tab-content">
                        <div className="tab-pane fade active show" id="infoTab" role="tabpanel">
                        {isLoading ? (
                            <div className="w-100 d-flex align-items-center justify-content-center spinner-wrapper">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Yükleniyor...</span>
                                </div>
                            </div>
                        ) : !acente ? (
                            <div className="card card-body text-center">Acente bulunamadı.</div>
                        ) : (
                            <>
                                <div className="card card-body lead-info">
                                    <div className="mb-4 d-flex align-items-center justify-content-between">
                                        <h5 className="fw-bold mb-0">
                                            <span className="d-block mb-2">Acente Bilgileri:</span>
                                        </h5>
                                        <button className="btn btn-light" onClick={openEditModal}>
                                            <i className="feather-edit me-2"></i>
                                            Düzenle
                                        </button>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Acente Adı</div>
                                        <div className="col-lg-10">{acente.Acente_Adi}</div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Temsilci Adı</div>
                                        <div className="col-lg-10">{parseCommaList(acente.Temsilci_Adi).join(', ') || 'Boş'}</div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Uyruk</div>
                                        <div className="col-lg-10">{acente.Uyruk || 'Boş'}</div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Telefon</div>
                                        <div className="col-lg-10 d-flex justify-content-start align-items-center">
                                            {acente.Telefon ? (
                                                <>
                                                    <a href={`tel:${formatPhoneTR(acente.Telefon).tel}`}>{formatPhoneTR(acente.Telefon).display}</a>
                                                    <a href={`https://wa.me/${formatPhoneTR(acente.Telefon).wa}`} target="_blank" rel="noreferrer" className="btn btn-success rounded-circle ms-2 p-1" style={{ width: '30px', height: '30px' }}><i className="bi bi-whatsapp"></i></a>
                                                </>
                                            ) : 'Boş'}
                                        </div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Email</div>
                                        <div className="col-lg-10"><a href={`mailto:${acente.Email}`}>{acente.Email || 'Boş'}</a></div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Son Arama Tarihi</div>
                                        <div className="col-lg-10">{formatDateTimeDisplay(acente.Son_Arama_Tarihi)}</div>
                                    </div>
                                </div>
                                <hr />
                                <div className="card card-body general-info">
                                    <div className="mb-4 d-flex align-items-center justify-content-between">
                                        <h5 className="fw-bold mb-0">
                                            <span className="d-block mb-2">Genel Bilgiler:</span>
                                        </h5>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Oluşturma Tarihi</div>
                                        <div className="col-lg-10">{formatDateTimeDisplay(acente.Olusturma_Tarihi)}</div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Arama Sayısı</div>
                                        <div className="col-lg-10">
                                            <span className="badge bg-primary fs-13" style={{ fontWeight: 700 }}>{acente.Arama_Sayisi || 0}</span>
                                        </div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Hatırlatma Tarihi</div>
                                        <div className="col-lg-10 hstack gap-1">
                                            <span>{acente.Hatirlatma_Tarihi && new Date(acente.Hatirlatma_Tarihi).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '')}</span>
                                            <button
                                                className="text-primary border-0 bg-transparent p-0"
                                                data-bs-toggle="modal"
                                                data-bs-target="#editReminder"
                                            >
                                                <i className="feather-edit" style={{ fontSize: '14px' }}></i>
                                            </button>
                                            <button
                                                className="text-primary border-0 bg-transparent p-0"
                                                onClick={deleteReminder}
                                            >
                                                <i className="feather-trash-2" style={{ fontSize: '14px' }}></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Temsilci</div>
                                        <div className="col-lg-10 hstack gap-1">
                                            <span className="fw-semibold text-dark">{temsilciLabel}</span>
                                            {isAdmin && (
                                                <>
                                                    <button
                                                        className="text-primary border-0 bg-transparent p-0"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#editTemsilci"
                                                    >
                                                        <i className="feather-edit" style={{ fontSize: '14px' }}></i>
                                                    </button>
                                                    <button
                                                        className="text-primary border-0 bg-transparent p-0"
                                                        onClick={deleteTemsilci}
                                                    >
                                                        <i className="feather-trash-2" style={{ fontSize: '14px' }}></i>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Etiketler</div>
                                        <div className="col-lg-10 hstack gap-2">
                                            {currentEtiketler.map((etiket) => (
                                                <a
                                                    key={etiket.Etiket_ID}
                                                    href="javascript:void(0);"
                                                    onClick={() => deleteEtiket(etiket.Etiket_ID)}
                                                    className="badge"
                                                    style={{ backgroundColor: "#" + etiket.Etiket_Renk, color: "#" + etiket.Etiket_Yazi_Renk }}
                                                >
                                                    {etiket.Etiket_Adi}
                                                </a>
                                            ))}
                                            <button
                                                className="avatar-text avatar-sm"
                                                data-bs-toggle="modal"
                                                data-bs-target="#addEtiket"
                                            >
                                                <i className="feather-plus" style={{ fontSize: '14px' }}></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-lg-2 fw-medium">Açıklama</div>
                                        <div className="col-lg-10 hstack gap-1">
                                            {acente.Aciklama}
                                            <button
                                                className="text-primary border-0 bg-transparent p-0"
                                                data-bs-toggle="modal"
                                                data-bs-target="#editAciklama"
                                            >
                                                <i className="feather-edit" style={{ fontSize: '14px' }}></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        </div>
                        <div className="tab-pane fade" id="notesTab" role="tabpanel">
                            <div className="card card-body">
                                <div className="row note-has-grid" id="note-full-container">
                                    {notes.length > 0 ? (
                                        <>
                                            {notes.map((not, index) => (
                                                <div key={"note_" + not.Not_ID} className="col-xxl-4 col-xl-6 col-lg-4 col-sm-6 single-note-item all-category note-important note-tasks">
                                                    <div className="card card-body mb-4 stretch stretch-full">
                                                        <span className="side-stick"></span>
                                                        <button onClick={(e) => viewNote(not.Not_ID)} data-bs-toggle="modal" data-bs-target="#viewNote" className="avatar-text avatar-md" style={{ position: 'absolute', right: '15px', top: '15px' }}><i className="feather-eye"></i></button>
                                                        <h5 className="note-title text-truncate w-75 mb-1" data-noteheading={not.Not_Basligi}>{not.Not_Basligi}</h5>
                                                        <p className="fs-11 text-muted note-date">{not.Not_Tarihi}</p>
                                                        <div className="note-content flex-grow-1">
                                                            <p className="text-muted note-inner-content text-truncate-3-line" data-notecontent={not.Not_Aciklamasi}>{not.Not_Aciklamasi}</p>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-1">
                                                            <button onClick={(e) => deleteNote(not.Not_ID)} className="avatar-text avatar-md"><i className="feather-trash-2 remove-note"></i></button>
                                                            <div className="ms-auto d-flex gap-2 align-items-center">
                                                                <img src={not.Kullanici.Profil_Gorseli} className="avatar avatar-sm" />
                                                                <span className="fw-bold">{not.Kullanici.Kullanici_Adi} {not.Kullanici.Kullanici_Soyadi}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="col-xxl-4 col-xl-6 col-lg-4 col-sm-6 single-note-item all-category note-important note-tasks">
                                                <div className="card card-body mb-4 strech strech-full">
                                                    <span className="side-stick"></span>
                                                    <h5 className="note-title text-truncate w-75 mb-1" data-noteheading=""></h5>
                                                    <p className="fs-11 text-muted note-date"></p>
                                                    <div className="note-content flex-grow-1">
                                                        <p className="text-muted note-inner-content text-truncate-3-line" data-notecontent=""></p>
                                                    </div>
                                                    <p></p>
                                                    <a href="javascript:void(0);" className="avatar-text bg-soft-primary text-primary mx-auto" data-bs-toggle="modal" data-bs-target="#addNote" data-bs-tooltip="tooltip" title="Yeni Not">
                                                        <i className="feather-plus"></i>
                                                    </a>
                                                    <span></span>
                                                    <div className="d-flex align-items-center gap-1 invisible pe-none">
                                                        <button className="avatar-text avatar-md"><i className="feather-trash-2 remove-note"></i></button>
                                                        <div className="ms-auto d-flex gap-2 align-items-center">
                                                            <img src="" className="avatar avatar-sm" />
                                                            <span className="fw-bold">LEVELUP CRM</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center" style={{ height: "calc(100vh - 315px)" }}>
                                            <div className="text-center">
                                                <h2 className="fs-16 fw-semibold">Not bulunamadı!</h2>
                                                <p className="fs-12 text-muted">Henüz herhangi bir görüşme notu kayıt edilmemiş.</p>
                                                <a href="javascript:void(0);" className="avatar-text bg-soft-primary text-primary mx-auto" data-bs-toggle="modal" data-bs-target="#addNote" data-bs-tooltip="tooltip" title="Yeni Not">
                                                    <i className="feather-plus"></i>
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>
                </div>

                <div className="modal fade-scale" id="addNote" ref={addNoteRef} tabIndex="-1" aria-labelledby="addNote" aria-hidden="true" data-bs-backdrop="false">
                    <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2 className="d-flex flex-column mb-0">
                                    <span className="fs-18 fw-bold mb-1">Not Ekle</span>
                                </h2>
                                <a href="javascript:void(0)" className="avatar-text avatar-md bg-soft-danger close-icon" data-bs-dismiss="modal">
                                    <i className="feather-x text-danger"></i>
                                </a>
                            </div>
                            <div className="modal-body p-0">
                                <div className="px-3 w-100 d-flex border-bottom align-items-center">
                                    <input className="form-control border-0 my-1 w-100 shadow-none" type="text" placeholder="Başlık" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} />
                                </div>
                                <div className="px-3 w-100 d-flex align-items-center">
                                    <textarea className="form-control border-0 my-1 w-100 shadow-none" type="text" rows={3} placeholder="İçerik" value={noteDesc} onChange={(e) => setNoteDesc(e.target.value)}></textarea>
                                </div>
                            </div>

                            <div className="modal-footer d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <div className="dropdown me-2">
                                        <a href="javascript:void(0)" onClick={createNote} className="btn btn-primary" data-bs-toggle="tooltip" data-bs-trigger="hover" title="" data-bs-original-title="Editing Actions">
                                            <span className="me-2">
                                                <i className="feather-plus"></i>
                                            </span>
                                            Ekle
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal fade-scale" id="viewNote" ref={viewNoteRef} tabIndex="-1" aria-labelledby="viewNote" aria-hidden="true" data-bs-backdrop="false">
                    <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                        {selectedNote && (
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h2 className="d-flex flex-column mb-0">
                                        <span className="fs-18 fw-bold mb-1">Not Ekle</span>
                                    </h2>
                                    <a href="javascript:void(0)" className="avatar-text avatar-md bg-soft-danger close-icon" data-bs-dismiss="modal">
                                        <i className="feather-x text-danger"></i>
                                    </a>
                                </div>
                                <div className="modal-body p-0">
                                    <div className="px-3 mt-3 w-100">
                                        <label className='form-label'>Not Başlığı</label>
                                        <input className="form-control border-0 my-1 w-100 shadow-none" type="text" placeholder="Başlık" value={selectedNote.Not_Basligi} readOnly />
                                    </div>
                                    <div className="px-3 mt-3 w-100">
                                        <label className='form-label'>Not Tarihi</label>
                                        <input className="form-control border-0 my-1 w-100 shadow-none" type="text" placeholder="Tarih" value={selectedNote.Not_Tarihi} readOnly />
                                    </div>
                                    <div className="px-3 mt-3 w-100">
                                        <label className='form-label'>Not Açıklaması</label>
                                        <textarea id="viewNoteDesc" className="form-control border-0 my-1 w-100 shadow-none" type="text" placeholder="İçerik" value={selectedNote.Not_Aciklamasi} readOnly></textarea>
                                    </div>
                                </div>

                                <div className="modal-footer d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                        <a href="javascript:void(0)" className="btn btn-secondary" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-dismiss="modal" title="" data-bs-original-title="Editing Actions">
                                            <span className="me-2">
                                                <i className="feather-x"></i>
                                            </span>
                                            Kapat
                                        </a>
                                        <a href="javascript:void(0)" className="btn btn-danger" onClick={(e) => deleteNote(selectedNote.Not_ID)} data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-dismiss="modal" title="" data-bs-original-title="Editing Actions">
                                            <span className="me-2">
                                                <i className="feather-trash-2"></i>
                                            </span>
                                            Sil
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="modal fade-scale" id="editAciklama" ref={editAciklamaRef} tabIndex="-1" aria-labelledby="editAciklama" aria-hidden="true" data-bs-backdrop="false">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2 className="d-flex flex-column mb-0">
                                    <span className="fs-18 fw-bold mb-1">Açıklama Düzenle</span>
                                </h2>
                                <a href="javascript:void(0)" className="avatar-text avatar-md bg-soft-danger close-icon" data-bs-dismiss="modal">
                                    <i className="feather-x text-danger"></i>
                                </a>
                            </div>
                            <div className="modal-body">
                                <textarea
                                    className="form-control"
                                    rows={4}
                                    value={yeniAciklama}
                                    onChange={(e) => setYeniAciklama(e.target.value)}
                                    placeholder="Açıklama giriniz..."
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">İptal</button>
                                <button type="button" className="btn btn-primary" onClick={handleAciklamaUpdate}>Kaydet</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal fade-scale" id="editReminder" ref={editReminderRef} tabIndex="-1" aria-labelledby="editReminder" aria-hidden="true" data-bs-backdrop="false">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2 className="d-flex flex-column mb-0">
                                    <span className="fs-18 fw-bold mb-1">Hatırlatma Tarihi Düzenle</span>
                                </h2>
                                <a href="javascript:void(0)" className="avatar-text avatar-md bg-soft-danger close-icon" data-bs-dismiss="modal">
                                    <i className="feather-x text-danger"></i>
                                </a>
                            </div>
                            <div className="modal-body">
                                <input
                                    className="form-control"
                                    type="datetime-local"
                                    value={reminderDate}
                                    onChange={(e) => setReminderDate(e.target.value)}
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">İptal</button>
                                <button type="button" className="btn btn-primary" onClick={handleReminderUpdate}>Kaydet</button>
                            </div>
                        </div>
                    </div>
                </div>

                {isAdmin && (
                    <div className="modal fade-scale" id="editTemsilci" ref={editTemsilciRef} tabIndex="-1" aria-labelledby="editTemsilci" aria-hidden="true" data-bs-backdrop="false">
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h2 className="d-flex flex-column mb-0">
                                        <span className="fs-18 fw-bold mb-1">Temsilci Düzenle</span>
                                    </h2>
                                    <a href="javascript:void(0)" className="avatar-text avatar-md bg-soft-danger close-icon" data-bs-dismiss="modal">
                                        <i className="feather-x text-danger"></i>
                                    </a>
                                </div>
                                <div className="modal-body">
                                    <select ref={temsilciSelectRef} className="form-control form-select">
                                        <option value="">Seçiniz...</option>
                                        {temsilciler.map((temsilci) => (
                                            <option key={"temsilci-" + temsilci.value} value={temsilci.value} selected={acente?.Temsilci_ID === temsilci.value}>{temsilci.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">İptal</button>
                                    <button type="button" className="btn btn-primary" onClick={changeTemsilci}>Kaydet</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal fade-scale" id="addEtiket" ref={addEtiketRef} tabIndex="-1" aria-labelledby="addEtiket" aria-hidden="true" data-bs-backdrop="false">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2 className="d-flex flex-column mb-0">
                                    <span className="fs-18 fw-bold mb-1">Etiket Ekle</span>
                                </h2>
                                <a href="javascript:void(0)" className="avatar-text avatar-md bg-soft-danger close-icon" data-bs-dismiss="modal">
                                    <i className="feather-x text-danger"></i>
                                </a>
                            </div>
                            <div className="modal-body">
                                <Select2WColor
                                    value={newEtiket}
                                    key={"etiket-" + acenteId}
                                    options={etiketOptions.filter(etiket => !currentEtiketIds.includes(etiket.value))}
                                    isSearchable={true}
                                    onChange={(e) => setNewEtiket(e)}
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">İptal</button>
                                <button type="button" className="btn btn-primary" onClick={addEtiket}>Kaydet</button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {showEditModal && editForm && (
                <div className="modal fade show" style={{ display: 'block' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Acente Düzenle</h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Acente Adı</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.agencyName}
                                            onChange={(e) => setEditForm({ ...editForm, agencyName: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Temsilci Adı</label>
                                        <MultiTextInput
                                            values={editForm.representativeNames}
                                            onChange={(vals) => setEditForm({ ...editForm, representativeNames: vals })}
                                            placeholder="Temsilci adı giriniz"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Uyruk</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.nationality}
                                            onChange={(e) => setEditForm({ ...editForm, nationality: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Telefon</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.phone}
                                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.email}
                                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Son Arama Tarihi</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={editForm.date}
                                            onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>İptal</button>
                                    <button type="submit" className="btn btn-primary">Kaydet</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            {showEditModal && (
                <div style={modalBackdropStyle} onClick={() => setShowEditModal(false)} />
            )}
        </Layout>
    );
};

export default AcenteDetay;
